import React, {
    Component,
} from 'react';

import PropTypes from 'prop-types';

import { localize } from 'wanderu-i18n';

import Waypoint from '../../containers/Waypoint';
import Layover from './Layover/Layover';
import StopList from './StopList/StopList';
import Transfer from './Transfer/Transfer';
import Events from './events/ItineraryEvents';

import styles from './Itinerary.scss';

class Itinerary extends Component {
    static propTypes = {
        arrival: PropTypes.object.isRequired,
        arriveIsNearby: PropTypes.bool,
        carrier: PropTypes.object.isRequired,
        departIsNearby: PropTypes.bool,
        departure: PropTypes.object.isRequired,
        hideDepartureEndpoint: PropTypes.bool,
        itineraries: PropTypes.arrayOf(PropTypes.shape({
            itinerary: PropTypes.arrayOf(PropTypes.object).isRequired,
            metadata: PropTypes.object,
        })).isRequired,
        onGetItineraryInfo: PropTypes.func.isRequired,
        showArriveEndpoint: PropTypes.bool,
        showDepartEndpoint: PropTypes.bool,
        showMapPins: PropTypes.bool,
        tripInformation: PropTypes.shape({
            fareTitle: PropTypes.string,
            termsAndConditions: PropTypes.string,
        }),
        type: PropTypes.string.isRequired,
        seats: PropTypes.number.isRequired,
        wheelchairs: PropTypes.number.isRequired,
    };

    static defaultProps = {
        itineraries: [],
        showArriveEndpoint: true,
        showDepartEndpoint: true,
        showMapPins: false,
        departIsNearby: false,
        arriveIsNearby: false,
        tripInformation: {},
    };

    constructor(props) {
        super(props);

        this.state = {
            arriveStationId: '',
            departStationId: '',
            nearbyStation: false,
            showDirections: false,
            loadingPartId: undefined,
        };
    }

    componentDidUpdate(oldProps) {
        const didLoad = this.props.itineraries.some((leg, idx) => {
            return leg.metadata.complete
                && oldProps.itineraries[idx]
                && !oldProps.itineraries[idx].metadata.complete;
        });

        if (didLoad) {
            this.setState(() => ({
                loadingPartId: undefined,
            }));
        }
    }

    _triggerDirectionsLinkEvent() {
        const {
            tripId,
            type,
        } = this.props;
        const {
            departStationId,
        } = this.state;

        if (this.state.showDirections) {
            Events.directionsLinkOpened({
                eventCategory: type,
                stationId: departStationId,
                tripId,
            });
        }
    }

    onLoadStopList = (loadingPartId, metadata) => {
        const {
            onGetItineraryInfo,
        } = this.props;
        const {
            complete,
            tripId,
        } = metadata;

        if (complete) {
            return;
        }

        this.setState(() => {
            return {
                loadingPartId,
            };
        });

        onGetItineraryInfo(tripId);
    }

    renderRoutedTripTransfer(arriveLeg = {}, departLeg = {}) {
        const {
            arrival,
            departure,
            type,
        } = this.props;
        const {
            name: arriveStationName,
            timestamp: arriveLegTimestamp,
            arrivalStationId,
        } = arriveLeg;
        const {
            name: departStationName,
            timestamp: departLegTimestamp,
            departureStationId,
        } = departLeg;
        const partId = `${arriveLeg.tripId}-${departLeg.tripId}`;
        const transferType = arriveStationName === departStationName
            ? 'transfer'
            : 'selfTransfer';
        const nearby = arrival.nearby || departure.nearby;
        const arriveTimestamp = transferType === 'selfTransfer'
            ? departLegTimestamp
            : arriveLegTimestamp;
        const departTimestamp = transferType === 'selfTransfer'
            ? arriveLegTimestamp
            : departLegTimestamp;

        return (
            <Transfer
                arriveStationId={departureStationId}
                arriveTimestamp={arriveTimestamp}
                departStationId={arrivalStationId}
                departTimestamp={departTimestamp}
                itineraryType={type}
                key={partId}
                nearby={nearby}
                tripId={partId}
                type={transferType}
            />
        );
    }

    renderItineraryItem = (
        item,
        itineraryMetadata,
        containsJourneyDepart,
        containsJourneyArrive,
        isFirstItemInLeg,
    ) => {
        const {
            arrival,
            departure,
            tripId,
            tripInformation,
            price,
            itineraryError,
            wheelchairs,
            seats,
            departIsNearby,
            arriveIsNearby,
            type,
        } = this.props;
        const {
            loadingPartId,
        } = this.state;

        const departWaypoint = item.waypointType === 'depart';
        const arriveWaypoint = item.waypointType === 'arrive';
        const transferNearby = departWaypoint
            ? departure.nearby
            : arrival.nearby;
        const carrierId = item.carrierId;

        switch (item.type) {
            case 'waypoint':
                const endpoint = (containsJourneyArrive && arriveWaypoint)
                    || (containsJourneyDepart && departWaypoint);
                const stationId = departWaypoint
                    ? item.departureStationId
                    : item.arrivalStationId;

                const isNearby = (containsJourneyDepart && departWaypoint && departIsNearby)
                    || (containsJourneyArrive && arriveWaypoint && arriveIsNearby);

                return (
                    <Waypoint
                        {...item}
                        carrierId={carrierId}
                        endpoint={endpoint}
                        key={item.partId}
                        nearby={isNearby}
                        price={price}
                        seats={seats}
                        showDisplayName={departWaypoint}
                        showPrice={isFirstItemInLeg}
                        stationId={stationId}
                        tripInformation={tripInformation}
                        type={type}
                        wheelchairs={wheelchairs}
                    />
                );

            case 'layover':
                return (
                    <Layover
                        {...item}
                        key={item.partId}
                    />
                );

            case 'selfTransfer':
            case 'transfer':
                return (
                    <Transfer
                        {...item}
                        key={item.partId}
                        nearby={transferNearby}
                    />
                );

            case 'stopList':
                return (
                    <StopList
                        {...item}
                        itineraryError={itineraryError}
                        key={item.partId}
                        loading={item.partId === loadingPartId}
                        metadata={itineraryMetadata}
                        onLoadStopList={this.onLoadStopList}
                        tripId={tripId}
                    />
                );

            default:
                console.warn(`itinerary type ${item.type} not supported`);

                return null;
        }
    }

    renderItineraryLeg = (itineraryComponents, leg, legIdx) => {
        const {
            arrival,
            departure,
            itineraries,
            showArriveEndpoint,
            showDepartEndpoint,
        } = this.props;
        const {
            itinerary,
            metadata,
        } = leg;

        const isFirstLeg = legIdx === 0;
        const isLastLeg = legIdx === itineraries.length - 1;
        const componentsToAdd = itinerary.map((item, idx) => {
            /* when itineraries are part of a larger "journey" (e.g. a routed
             * trip on the tripsummary page where the first leg is deeplink and
             * the second is not), we only want to indicate that the very first
             * departure and the very last arrival are the endpoints */
            const isFirstItemInLeg = idx === 0;
            const isFirstDepartureEndpoint = itinerary[idx].departureStationId === departure.stationId;
            const isLastItemInLeg = idx === itinerary.length - 1;
            const isLastArrivalEndpoint = itinerary[idx].arrivalStationId === arrival.stationId;
            const containsJourneyArrive = showArriveEndpoint
                && isLastLeg
                && isLastItemInLeg
                && isLastArrivalEndpoint;
            const containsJourneyDepart = showDepartEndpoint
                && isFirstLeg
                && isFirstItemInLeg
                && isFirstDepartureEndpoint;

            return this.renderItineraryItem(
                item,
                metadata,
                containsJourneyDepart,
                containsJourneyArrive,
                isFirstItemInLeg,
            );
        });

        if (!isLastLeg) {
            /* build a transfer component out of the last item in this itinerary
             * and the first item in the next itinerary */
            componentsToAdd.push(this.renderRoutedTripTransfer(
                itineraries[legIdx].itinerary[itineraries[legIdx].itinerary.length - 1],
                itineraries[legIdx + 1].itinerary[0],
            ));
        }

        return [
            ...itineraryComponents,
            ...componentsToAdd,
        ];
    }

    render() {
        const {
            className,
            itineraries,
        } = this.props;

        const hideItinerary = itineraries.some(({ itinerary = [] }) => !itinerary.length);

        /* defends against bad data by hiding the itinerary trip line. Will still
         * allow interaction with the show-details dropdown to view other information
         * such as amenities */
        if (hideItinerary) {
            return null;
        }

        return (
            <>
                <div className={`${className} ${styles.itinerary}`}>
                    {itineraries.reduce(this.renderItineraryLeg, [])}
                </div>
            </>
        );
    }
}

export { Itinerary };
export default localize(Itinerary);
